import { useTranslation } from "react-i18next";
import { Formik, Form } from "formik";
import { useContext, useState } from "react";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import ErrorMessage from "../errorMessage/ErrorMessage";
import Spinner from "../spinner/Spinner";
import Select from "react-select";
import measurementTypes from "../../resources/json/measurementTypes.json";
import {Consts} from "../pages/consts";
import useDialogDrag from "../../hooks/useDialogDrag";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {getInputFocusStyle} from "../pages/utils";

const Measurement = ({
  selectedPatient,
  onSaveNewMeasurement,
  showDialog,
  hcpView,
}) => {
  const { t } = useTranslation();
  const [measurementValues, setMeasurementValues] = useState([]);
  const [selectedMeasurementType, setSelectedMeasurementType] = useState({});
  const context = useContext(InitContext);
  const [startDate, setStartDate] = useState();
  const { loading, error, clearError, saveDbMeasurement } = useMmcService();
  const { dragStyle, isDragging, onDragHandleMouseDown } = useDialogDrag(showDialog);
  const [isTextFieldFocused, setIsTextFieldFocused] = useState(false);

  const handleSaveMeasurement = () => {
    clearError();
    const params = [];

    measurementValues?.forEach((measurementValue, i) => {
      if (measurementValue) {
        params?.push({
          name: selectedMeasurementType?.value?.params[i],
          value: measurementValue,
          unit: selectedMeasurementType?.value?.units[i],
        });
      }
    });

    const measurementToSave = {
      name: selectedMeasurementType?.label,
      dateTime: startDate,
      params: params,
    };

    const patientId = !selectedPatient
      ? context?.patient?.id
      : selectedPatient?.id;

    saveDbMeasurement(context?.token, patientId, measurementToSave).then(() => {
      onSaveNewMeasurement(measurementToSave);
    });
  };

  const checkRequiredFields = () => {
    return (
      selectedMeasurementType &&
      startDate &&
      measurementValues?.length > 0
    );
  };

  const handleSelectMeasurementType = async (measurementType) => {
    setSelectedMeasurementType(measurementType);
    setMeasurementValues(null);
  };

  const handleSettingParamValue = async (value, index) => {
    setMeasurementValues((previousValues) => {
      let updatedValues = previousValues ? [...previousValues] : []; // initial array

      if(value){ // if value is not empty, set it
        updatedValues[index] = value;
      } else { // if value is empty, it means that the existing value on this place should be deleted
        if(previousValues) { // check if the array already has items
          updatedValues = previousValues?.filter((item, i) => i !== index); // delete the existing value
        }
      }

      return updatedValues;
    });
  };

  const errorMessage = error && (
    <ErrorMessage message={t("error_by_saving_a_measurement")} />
  );

  const spinner = loading && <Spinner />;

  return (
    <div className="w3-modal" style={{ display: showDialog ? Consts.BLOCK : Consts.NONE }}>
      <div
        className={`w3-modal-content w3-card-4 w3-animate-zoom form-panel dialog-medium add-drug-dialog measurement-dialog dialog-draggable ${isDragging ? "dialog-draggable--dragging" : ""}`}
        style={dragStyle}
      >
        <Formik
          initialValues={{
            name: "",
            dateTime: "",
            comment: "",
          }}
          onSubmit={handleSaveMeasurement}
        >
          {({}) => (
            <Form>
              <header className="dialog-drag-header" onMouseDown={onDragHandleMouseDown}>
                <strong className="dialog-drag-header__title">
                  {hcpView
                    ? t("please_enter_the_measurement_data")
                    : t("please_enter_your_measurement_data")}
                </strong>
                <button
                  type="button"
                  className="dialog-drag-close"
                  onClick={() => onSaveNewMeasurement(null)}
                  aria-label={t("close", { defaultValue: "Close" })}
                  title={t("close", { defaultValue: "Close" })}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </header>
              {errorMessage}
              {spinner}
              <div className="w3-row w3-padding-small measurement-form-row">
                <div className="w3-third measurement-form-label">
                  <label>{t("measurement")}</label>
                </div>
                <div className="w3-twothird">
                  <Select
                    id="name"
                    options={measurementTypes}
                    onChange={(measurementType) =>
                      handleSelectMeasurementType(measurementType)
                    }
                    formatOptionLabel={(measurementType) =>
                      t(measurementType.label)
                    }
                    placeholder={t("select")}
                    className="custom-select meds-sort-select symptom-compact-select"
                    classNamePrefix="meds-sort-select"
                    isSearchable={false}
                    menuPlacement="auto"
                    maxMenuHeight={280}
                  />
                </div>
              </div>
              <div className="w3-row w3-padding-small measurement-form-row">
                <div className="w3-third measurement-form-label">
                  <label>{t("measurement_date")}:</label>
                </div>
                <div className="w3-twothird">
                  <input
                    id={Consts.DATE}
                    type={Consts.DATE}
                    className="w3-input w3-border w3-round form-input symptom-edit-input measurement-input"
                    onChange={(event) => setStartDate(event.target.value)}
                  />
                </div>
              </div>
              {selectedMeasurementType?.value?.params?.map((param, i) => {
                return (
                  <div
                    className="w3-row w3-padding-small measurement-param-row"
                    key={`param${selectedMeasurementType.label}${i}`}
                  >
                    <label className="w3-third measurement-form-label">{t(param)}:</label>
                    <input
                      id={Consts.VALUE}
                      name={Consts.VALUE}
                      type={Consts.NUMBER}
                      min="0"
                      onFocus={() => setIsTextFieldFocused(true)}
                      onBlur={() => setIsTextFieldFocused(false)}
                      className="form-input w3-margin-right symptom-edit-input measurement-input"
                      defaultValue={measurementValues ? measurementValues[i] : ""}
                      onChange={(event) =>
                        handleSettingParamValue(event.target.value, i)
                      }
                      step="0.01"
                      style={getInputFocusStyle(isTextFieldFocused)}
                    />
                    <label className="measurement-unit-label">{t(selectedMeasurementType?.value?.units[i])}</label>
                  </div>
                );
              })}
              <footer className="w3-container symptom-edit-footer">
                <div className="w3-right symptom-edit-footer-actions">
                  <button
                    type="submit"
                    className="symptom-ui-button symptom-ui-button--solid-primary symptom-edit-submit-button"
                    disabled={!checkRequiredFields()}
                  >
                    {t("submit")}
                  </button>
                </div>
              </footer>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Measurement;
